import React, { useEffect, useState } from "react";
import axios from "axios";

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
  "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
  "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
  "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico",
  "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
  "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont",
  "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
];

const DEGREES = ["MD", "DO", "NP", "PA", "RN", "MA", "BS", "BA"];

const SPECIALTIES = [
  "Allergy and Immunology",
  "Anesthesiology",
  "Cardiology",
  "Cardiovascular Surgery",
  "Child and Adolescent Psychiatry",
  "Clinical Genetics",
  "Colon and Rectal Surgery",
  "Critical Care Medicine",
  "Dermatology",
  "Emergency Medicine",
  "Endocrinology",
  "Family Medicine",
  "Gastroenterology",
  "General Surgery",
  "Geriatrics",
  "Hematology",
  "Infectious Disease",
  "Internal Medicine",
  "Interventional Radiology",
  "Medical Oncology",
  "Neonatology",
  "Nephrology",
  "Neurology",
  "Neurosurgery",
  "Nuclear Medicine",
  "Obstetrics and Gynecology",
  "Occupational Medicine",
  "Ophthalmology",
  "Orthopedic Surgery",
  "Otolaryngology (ENT)",
  "Pain Management",
  "Palliative Care",
  "Pathology",
  "Pediatrics",
  "Physical Medicine and Rehabilitation",
  "Plastic Surgery",
  "Podiatry",
  "Preventive Medicine",
  "Primary Care",
  "Psychiatry",
  "Pulmonology",
  "Radiation Oncology",
  "Radiology",
  "Rheumatology",
  "Sleep Medicine",
  "Sports Medicine",
  "Thoracic Surgery",
  "Transplant Surgery",
  "Trauma Surgery",
  "Urology",
  "Vascular Surgery"
];

export default function AuthForm({ onSubmit, label }) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    country_code: "+1",
    phone_number: "",
    password: "",
    confirmPassword: "",
    state: "",
    city: "",
    degree: "",
    specialty: "",
    practice_name: "",
    privacy_policy_agreed: false,
    terms_of_service_agreed: false,
    invite_code: "",
  });

  // Ask the backend whether signups currently require an invite code
  const [inviteCodeRequired, setInviteCodeRequired] = useState(false);
  useEffect(() => {
    if (label !== "Register") return;
    axios
      .get(`${process.env.REACT_APP_API}/registration-config`)
      .then((res) => setInviteCodeRequired(!!res.data.invite_code_required))
      .catch(() => setInviteCodeRequired(false));
  }, [label]);

  const handleChange = (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [e.target.name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate password confirmation for registration
    if (label === "Register") {
      if (form.password !== form.confirmPassword) {
        alert("Passwords do not match. Please try again.");
        return;
      }

      // Combine country code with phone number
      const submissionData = {
        ...form,
        phone_number: form.country_code + form.phone_number
      };
      delete submissionData.country_code;
      delete submissionData.confirmPassword;
      onSubmit(submissionData);
    } else {
      onSubmit(form);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-md mx-auto mt-10 bg-white p-6 rounded shadow space-y-4"
    >
      {label === "Register" && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">First Name</label>
              <input
                name="first_name"
                placeholder="First Name"
                onChange={handleChange}
                value={form.first_name}
                className="w-full border border-gray-300 rounded px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Last Name</label>
              <input
                name="last_name"
                placeholder="Last Name"
                onChange={handleChange}
                value={form.last_name}
                className="w-full border border-gray-300 rounded px-3 py-2"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">State</label>
              <select
                name="state"
                onChange={handleChange}
                value={form.state}
                className="w-full border border-gray-300 rounded px-3 py-2"
                required
              >
                <option value="">Select State</option>
                {US_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">City</label>
              <input
                name="city"
                placeholder="City"
                onChange={handleChange}
                value={form.city}
                className="w-full border border-gray-300 rounded px-3 py-2"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Degree</label>
              <select
                name="degree"
                onChange={handleChange}
                value={form.degree}
                className="w-full border border-gray-300 rounded px-3 py-2"
                required
              >
                <option value="">Select Degree</option>
                {DEGREES.map((degree) => (
                  <option key={degree} value={degree}>
                    {degree}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Specialty</label>
              <select
                name="specialty"
                onChange={handleChange}
                value={form.specialty}
                className="w-full border border-gray-300 rounded px-3 py-2"
                required
              >
                <option value="">Select Specialty</option>
                {SPECIALTIES.map((specialty) => (
                  <option key={specialty} value={specialty}>
                    {specialty}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Practice Name (optional)
            </label>
            <input
              name="practice_name"
              placeholder="Practice Name"
              onChange={handleChange}
              value={form.practice_name}
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>
        </>
      )}

      {label === "Register" && (
        <div>
          <label className="block text-sm font-medium mb-1">
            {inviteCodeRequired ? "Invite Code" : "Access Code (optional)"}
          </label>
          <input
            name="invite_code"
            placeholder="PLX-XXXX-XXXX"
            onChange={handleChange}
            value={form.invite_code}
            required={inviteCodeRequired}
            className="w-full border px-3 py-2 rounded uppercase"
          />
          <p className="text-xs text-gray-500 mt-1">
            {inviteCodeRequired
              ? "PLEXYS is currently invite-only. Enter the code you received."
              : "Have an access code? Enter it here — otherwise your 7-day free trial starts automatically."}
          </p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-1">Email</label>
        <input
          name="email"
          placeholder="Email"
          onChange={handleChange}
          value={form.email}
          className="w-full border border-gray-300 rounded px-3 py-2"
          type="email"
          autoComplete={label === "Register" ? "email" : "username"}
          required
        />
      </div>

      {label === "Register" && (
        <div>
          <label className="block text-sm font-medium mb-1">Phone Number</label>
          <div className="flex gap-2">
            <select
              name="country_code"
              onChange={handleChange}
              value={form.country_code}
              className="border border-gray-300 rounded px-3 py-2"
              required
            >
              <option value="+1">+1 (US/Canada)</option>
              <option value="+44">+44 (UK)</option>
              <option value="+91">+91 (India)</option>
              <option value="+86">+86 (China)</option>
              <option value="+81">+81 (Japan)</option>
              <option value="+49">+49 (Germany)</option>
              <option value="+33">+33 (France)</option>
              <option value="+39">+39 (Italy)</option>
              <option value="+34">+34 (Spain)</option>
              <option value="+61">+61 (Australia)</option>
              <option value="+55">+55 (Brazil)</option>
              <option value="+52">+52 (Mexico)</option>
            </select>
            <input
              name="phone_number"
              placeholder="5551234567"
              onChange={handleChange}
              value={form.phone_number}
              className="flex-1 border border-gray-300 rounded px-3 py-2"
              type="tel"
              pattern="[0-9]{10}"
              autoComplete="tel-national"
              required
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            For SMS verification. Enter 10-digit phone number.
          </p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-1">Password</label>
        <input
          type="password"
          name="password"
          placeholder="Password"
          onChange={handleChange}
          value={form.password}
          className="w-full border border-gray-300 rounded px-3 py-2"
          autoComplete={label === "Register" ? "new-password" : "current-password"}
          minLength={label === "Register" ? 10 : undefined}
          required
        />
        {label === "Register" && (
          <p className="text-xs text-gray-500 mt-1">
            Use at least 10 characters with uppercase, lowercase, number, and special character.
          </p>
        )}
      </div>

      {label === "Register" && (
        <div>
          <label className="block text-sm font-medium mb-1">Confirm Password</label>
          <input
            type="password"
            name="confirmPassword"
            placeholder="Confirm Password"
            onChange={handleChange}
            value={form.confirmPassword}
            className="w-full border border-gray-300 rounded px-3 py-2"
            autoComplete="new-password"
            minLength={10}
            required
          />
        </div>
      )}

      {label === "Register" && (
        <>
          <div className="space-y-3 pt-2">
            <div className="flex items-start">
              <input
                type="checkbox"
                name="privacy_policy_agreed"
                id="privacy_policy_agreed"
                onChange={handleChange}
                checked={form.privacy_policy_agreed}
                className="mt-1 mr-2"
                required
              />
              <label htmlFor="privacy_policy_agreed" className="text-sm text-gray-700">
                I have read and agree to the{" "}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline hover:opacity-80"
                  style={{ color: "#30853A" }}
                >
                  Privacy Policy
                </a>
              </label>
            </div>

            <div className="flex items-start">
              <input
                type="checkbox"
                name="terms_of_service_agreed"
                id="terms_of_service_agreed"
                onChange={handleChange}
                checked={form.terms_of_service_agreed}
                className="mt-1 mr-2"
                required
              />
              <label htmlFor="terms_of_service_agreed" className="text-sm text-gray-700">
                I have read and agree to the{" "}
                <a
                  href="/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline hover:opacity-80"
                  style={{ color: "#30853A" }}
                >
                  Terms of Service
                </a>
              </label>
            </div>
          </div>
        </>
      )}

      <button
        type="submit"
        className="w-full bg-primary text-white px-4 py-2 rounded font-semibold shadow hover:opacity-90 transition"
        style={{ backgroundColor: "#30853A" }}
      >
        {label}
      </button>
    </form>
  );
}
